// src/components/users/table/UserTable.tsx
import React from "react";
import ColumnFilter from "@/components/shared/ui/ColumnFilter";
import Input from "@/components/shared/ui/Input";
import Select from "@/components/shared/ui/Select";
import {
  FiEdit2,
  FiTrash2,
  FiGrid,         // Manage Modules
  FiRefreshCcw,  // Reset Password
} from "react-icons/fi";

/* ===========================================================================
 * Types
 * =========================================================================== */

export type Role = string;

export type UserTableFilters = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  role?: Role | "";
};

export type UserRow = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  roleName?: Role | null;
};

/* ===========================================================================
 * Component
 * =========================================================================== */

export default function UserTable({
  rows,
  pageRows,
  serialStart,
  filters,
  setFilters,
  onEdit,
  onDelete,
  onManageModules,
  onResetPassword,
  loading = false,
}: {
  rows: UserRow[];
  pageRows: UserRow[];
  serialStart: number;
  filters: UserTableFilters;
  setFilters: React.Dispatch<React.SetStateAction<UserTableFilters>>;
  onEdit: (u: UserRow) => void;
  onDelete: (u: UserRow) => void;
  onManageModules?: (u: UserRow) => void;
  onResetPassword?: (u: UserRow) => void;
  loading?: boolean;
}) {
  const showEmpty = !loading && pageRows.length === 0;
  const showLoading = loading && pageRows.length === 0;

  // Use all rows to generate role filter options
  const dynamicRoleOptions = Array.from(
    new Set(rows.map((u) => u.roleName).filter(Boolean))
  ) as string[];

  return (
    // Square bottom on purpose: the pagination bar below completes this card
    // and owns the bottom corners.
    <div className="overflow-hidden rounded-t-2xl border border-gray-200 bg-white">
      {/* Desktop table */}
      <div className="hidden overflow-x-auto md:block">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50 text-gray-600">
            <tr className="border-b border-gray-200">
              <Th>S.No</Th>

              <ThWithFilter
                label="First name"
                value={filters.firstName}
                onChange={(v) => setFilters((f) => ({ ...f, firstName: v }))}
              />

              <ThWithFilter
                label="Last name"
                value={filters.lastName}
                onChange={(v) => setFilters((f) => ({ ...f, lastName: v }))}
              />

              <ThWithFilter
                label="Email"
                value={filters.email}
                onChange={(v) => setFilters((f) => ({ ...f, email: v }))}
              />

              <ThWithFilter
                label="Phone"
                value={filters.phone}
                onChange={(v) => setFilters((f) => ({ ...f, phone: v }))}
              />

              <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wide">
                <div className="flex items-center gap-1.5">
                  <span>Role</span>
                  <ColumnFilter label="Role">
                    <Select
                      value={filters.role ?? ""}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          role: e.target.value as Role | "",
                        }))
                      }
                      options={dynamicRoleOptions}
                    />
                  </ColumnFilter>
                </div>
              </th>

              <Th className="text-right">Actions</Th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {showLoading ? (
              <LoadingRow colSpan={7} message="Loading users…" />
            ) : showEmpty ? (
              <LoadingRow colSpan={7} message="No users found." />
            ) : (
              pageRows.map((u, i) => (
                <tr key={u.id} className="hover:bg-gray-50/60">
                  <Td>{serialStart + i + 1}</Td>

                  <Td>
                    <UserAvatar first={u.firstName} last={u.lastName} />
                  </Td>

                  <Td>{u.lastName}</Td>

                  <Td className="max-w-[260px] truncate">{u.email}</Td>

                  <Td className="max-w-[180px] truncate">
                    {u.phone ?? "—"}
                  </Td>

                  <Td>
                    <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                      {u.roleName ?? "—"}
                    </span>
                  </Td>

                  <Td className="text-right">
                    <ActionsCell
                      row={u}
                      onManageModules={onManageModules}
                      onResetPassword={onResetPassword}
                      onEdit={onEdit}
                      onDelete={onDelete}
                    />
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile list */}
      <MobileList
        pageRows={pageRows}
        serialStart={serialStart}
        showEmpty={showEmpty}
        showLoading={showLoading}
        onManageModules={onManageModules}
        onResetPassword={onResetPassword}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
}

/* ===========================================================================
 * Sub Components
 * =========================================================================== */

function Th({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wide ${className}`}
    >
      {children}
    </th>
  );
}

function ThWithFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
}) {
  return (
    <Th>
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        <ColumnFilter label={label}>
          <Input
            placeholder="Contains…"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </ColumnFilter>
      </div>
    </Th>
  );
}

function Td({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={`px-5 py-3.5 ${className}`}>{children}</td>;
}

function UserAvatar({ first, last }: { first: string; last: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="grid h-8 w-8 place-items-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
        {(first?.[0] ?? "").toUpperCase()}
        {(last?.[0] ?? "").toUpperCase()}
      </div>
      <span className="text-gray-900">{first}</span>
    </div>
  );
}

function LoadingRow({ colSpan, message }: { colSpan: number; message: string }) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        className="px-5 py-10 text-center text-sm text-gray-500"
      >
        {message}
      </td>
    </tr>
  );
}

/* ===========================================================================
 * Actions Cell
 * =========================================================================== */

function ActionsCell({
  row,
  onManageModules,
  onResetPassword,
  onEdit,
  onDelete,
}: {
  row: UserRow;
  onManageModules?: (u: UserRow) => void;
  onResetPassword?: (u: UserRow) => void;
  onEdit: (u: UserRow) => void;
  onDelete: (u: UserRow) => void;
}) {
  return (
    <div className="flex items-center justify-end gap-1.5">
      {onManageModules && (
        <ActionButton
          icon={<FiGrid />}
          tooltip="Manage modules"
          onClick={() => onManageModules(row)}
          color="text-indigo-600 hover:bg-indigo-50"
        />
      )}

      {onResetPassword && (
        <ActionButton
          icon={<FiRefreshCcw />}
          tooltip="Reset password"
          onClick={() => onResetPassword(row)}
          color="text-sky-600 hover:bg-sky-50"
        />
      )}

      <ActionButton
        icon={<FiEdit2 />}
        tooltip="Edit"
        onClick={() => onEdit(row)}
        color="text-gray-600 hover:bg-gray-100"
      />

      <ActionButton
        icon={<FiTrash2 />}
        tooltip="Delete"
        onClick={() => onDelete(row)}
        color="text-red-600 hover:bg-red-50"
      />
    </div>
  );
}

function ActionButton({
  icon,
  tooltip,
  onClick,
  color,
}: {
  icon: React.ReactNode;
  tooltip: string;
  onClick: () => void;
  color: string;
}) {
  return (
    <button
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${color}`}
      onClick={onClick}
      aria-label={tooltip}
      title={tooltip}
    >
      {icon}
    </button>
  );
}

/* ===========================================================================
 * Mobile List
 * =========================================================================== */

function MobileList({
  pageRows,  
  showEmpty,
  showLoading,
  onManageModules,
  onResetPassword,
  onEdit,
  onDelete,
}: {
  pageRows: UserRow[];
  serialStart: number;
  showEmpty: boolean;
  showLoading: boolean;
  onManageModules?: (u: UserRow) => void;
  onResetPassword?: (u: UserRow) => void;
  onEdit: (u: UserRow) => void;
  onDelete: (u: UserRow) => void;
}) {
  return (
    <div className="divide-y divide-gray-200 md:hidden">
      {showLoading ? (
        <div className="p-6 text-center text-sm text-gray-500">
          Loading users…
        </div>
      ) : showEmpty ? (
        <div className="p-6 text-center text-sm text-gray-500">
          No users found.
        </div>
      ) : (
        pageRows.map((u
          // , i
        ) => (
          <div key={u.id} className="p-4">
            <div className="flex items-center justify-between">
              <UserAvatar first={u.firstName} last={u.lastName} />

              <div className="flex items-center gap-1">
                {onManageModules && (
                  <ActionButton
                    icon={<FiGrid />}
                    tooltip="Manage modules"
                    onClick={() => onManageModules(u)}
                    color="text-indigo-600 hover:bg-indigo-50"
                  />
                )}

                {onResetPassword && (
                  <ActionButton
                    icon={<FiRefreshCcw />}
                    tooltip="Reset password"
                    onClick={() => onResetPassword(u)}
                    color="text-sky-600 hover:bg-sky-50"
                  />
                )}

                <ActionButton
                  icon={<FiEdit2 />}
                  tooltip="Edit"
                  onClick={() => onEdit(u)}
                  color="text-gray-600 hover:bg-gray-100"
                />

                <ActionButton
                  icon={<FiTrash2 />}
                  tooltip="Delete"
                  onClick={() => onDelete(u)}
                  color="text-red-600 hover:bg-red-50"
                />
              </div>
            </div>

            <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-700">
              <div>
                <span className="text-gray-500">Phone:</span> {u.phone ?? "—"}
              </div>
              <div>
                <span className="text-gray-500">Role:</span>{" "}
                {u.roleName ?? "—"}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
